// Firebase functions for user profile management
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
  orderBy
} from "firebase/firestore";
import { updateProfile } from "firebase/auth";
import { db, auth } from "./firebase-config";
import { isAdminEmail } from "../../utils/adminUtils";
import { parseDisplayName, formatDisplayName } from "../../utils/nameUtils";
import { v4 as uuidv4 } from 'uuid';

// Get a user's profile data
export const getUserProfile = async (userId) => {
  try {
    console.log(`Fetching profile for user: ${userId}`);
    
    // Reference to the user's profile document
    const profileRef = doc(db, "user_profiles", userId);
    const profileSnap = await getDoc(profileRef);
    
    if (profileSnap.exists()) {
      console.log('Profile document found');
      return {
        success: true,
        data: profileSnap.data()
      };
    } else {
      console.log('No profile document found for this user');
      return {
        success: true,
        data: null
      };
    }
  } catch (error) {
    console.error("Error fetching user profile:", error);
    return { success: false, error: error.message };
  }
};

// Get a specific user's profile data by ID for viewing (public profile view)
export const getUserProfileById = async (userId, currentUserId = null) => {
  try {
    console.log(`Fetching public profile for user: ${userId}`);
    
    // Reference to the user's profile document
    const profileRef = doc(db, "user_profiles", userId);
    const profileSnap = await getDoc(profileRef);
    
    if (!profileSnap.exists()) {
      console.log('No profile document found for this user');
      return {
        success: false,
        error: 'User not found'
      };
    }
    
    const userData = profileSnap.data();
    
    // Check if user has completed onboarding
    if (userData.onboardingComplete !== true) {
      console.log('User has not completed onboarding');
      return {
        success: false,
        error: 'User profile not available'
      };
    }
    
    // Check if user is hidden - allow user to view their own profile even if hidden
    if (userData.isHidden === true && userId !== currentUserId) {
      console.log('User profile is hidden');
      return {
        success: false,
        error: 'User profile not available'
      };
    }
    
    // Parse names from displayName as source of truth
    const { firstName, lastName } = parseDisplayName(userData.displayName);
    
    // Transform data for public viewing - only include safe, public information
    const publicProfileData = {
      uid: userId,
      basicInfo: {
        firstName: firstName || userData.basicInfo?.firstName || '',
        lastName: lastName || userData.basicInfo?.lastName || '',
        currentRole: userData.basicInfo?.currentRole || '',
        companyName: userData.basicInfo?.companyName || '',
        companyId: userData.basicInfo?.companyId || '',
        location: userData.basicInfo?.location || '',
        photoURL: userData.profilePicture || userData.basicInfo?.photoURL || userData.photoURL || '',
        linkedinUrl: userData.basicInfo?.linkedinUrl || userData.profileLinks?.linkedin || '',
        email: userData.basicInfo?.email || userData.email || ''
      },
      preferences: {
        jobSearchStatus: userData.preferences?.jobSearchStatus || 'not_looking'
      }
    };
    
    console.log('Public profile data retrieved successfully');
    return {
      success: true,
      data: publicProfileData
    };
  } catch (error) {
    console.error("Error fetching user profile by ID:", error);
    return { success: false, error: error.message };
  }
};

// Update a user's profile data
export const updateUserProfile = async (userId, profileData, progressCallback = null) => {
  try {
    console.log(`Updating profile for user: ${userId}`);
    
    // Reference to the user's profile document
    const profileRef = doc(db, "user_profiles", userId);
    
    // Prepare profile data object without file object (can't be stored in Firestore)
    const { resumeFile, deleteResume, ...cleanProfileData } = profileData;
    
    // Handle name updates - sync with Firebase Auth displayName
    if (cleanProfileData.basicInfo && 
        (cleanProfileData.basicInfo.firstName !== undefined || cleanProfileData.basicInfo.lastName !== undefined)) {
      
      const currentUser = auth.currentUser;
      if (currentUser && currentUser.uid === userId) {
        // Get current names
        const currentDisplayName = currentUser.displayName || '';
        const { firstName: currentFirst, lastName: currentLast } = parseDisplayName(currentDisplayName);
        
        // Use provided names or fall back to current names
        const newFirstName = cleanProfileData.basicInfo.firstName !== undefined 
          ? cleanProfileData.basicInfo.firstName 
          : currentFirst;
        const newLastName = cleanProfileData.basicInfo.lastName !== undefined 
          ? cleanProfileData.basicInfo.lastName 
          : currentLast;
        
        // Update Firebase Auth displayName
        const newDisplayName = formatDisplayName(newFirstName, newLastName);
        if (newDisplayName !== currentDisplayName) {
          await updateProfile(currentUser, { displayName: newDisplayName });
          console.log(`Updated Firebase Auth displayName to: ${newDisplayName}`);
        }
        
        // Ensure basicInfo has the parsed names
        cleanProfileData.basicInfo = {
          ...cleanProfileData.basicInfo,
          firstName: newFirstName,
          lastName: newLastName,
          name: newDisplayName
        };
      }
    }
    
    // Combine with any additional data
    const dataToUpdate = {
      ...cleanProfileData,
      lastUpdated: serverTimestamp()
    };
    
    // Get existing document to check if we need to create or update
    const docSnap = await getDoc(profileRef);
    
    if (docSnap.exists()) {
      // Update existing document
      await updateDoc(profileRef, dataToUpdate);
    } else {
      // Create new document
      await setDoc(profileRef, {
        ...dataToUpdate,
        userId,
        createdAt: serverTimestamp()
      });
    }
    
    console.log('Profile updated successfully');
    
    return {
      success: true,
      data: cleanProfileData
    };
  } catch (error) {
    console.error("Error updating user profile:", error);
    return { success: false, error: error.message };
  }
};

// Save profile links for a user
export const saveProfileLinks = async (userId, links) => {
  try {
    console.log(`Saving profile links for user: ${userId}`);
    
    const profileRef = doc(db, "user_profiles", userId);
    
    await updateDoc(profileRef, {
      profileLinks: links,
      lastUpdated: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error("Error saving profile links:", error);
    return { success: false, error: error.message };
  }
};

// Update user's location
export const updateUserLocation = async (userId, locationDetails) => {
  try {
    console.log(`Updating location for user: ${userId}`);
    
    // Validate inputs
    if (!userId) {
      throw new Error('User ID is required');
    }
    
    if (!locationDetails) {
      throw new Error('Location details are required');
    }
    
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated. Please log in again.');
    }
    
    if (currentUser.uid !== userId) {
      throw new Error('Authentication mismatch. Please log in again.');
    }
    
    // Format location string for display
    const locationString = [
      locationDetails.city,
      locationDetails.state,
      locationDetails.country
    ].filter(Boolean).join(', ');
    
    if (!locationString) {
      throw new Error('Invalid location format');
    }
    
    // Update Firestore profile
    const profileRef = doc(db, "user_profiles", userId);
    await updateDoc(profileRef, {
      'basicInfo.location': locationString,
      'locationDetails': {
        id: locationDetails.id,
        city: locationDetails.city,
        state: locationDetails.state,
        country: locationDetails.country,
        coordinates: locationDetails.coordinates,
        formattedAddress: locationDetails.formattedAddress,
        updatedAt: serverTimestamp()
      },
      lastUpdated: serverTimestamp()
    });
    
    console.log('User location updated successfully');
    
    return {
      success: true,
      data: {
        location: locationString,
        locationDetails
      }
    };
  } catch (error) {
    console.error("Error updating user location:", error);
    return { 
      success: false, 
      error: error.message || 'An unexpected error occurred while updating your location'
    };
  }
};

// Update user's name and sync with Firebase Auth displayName
export const updateUserName = async (userId, firstName, lastName) => {
  try {
    console.log(`Updating name for user: ${userId}`);
    
    // Validate inputs
    if (!userId) {
      throw new Error('User ID is required');
    }
    
    if (!firstName && !lastName) {
      throw new Error('At least one name field is required');
    }
    
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated. Please log in again.');
    }
    
    if (currentUser.uid !== userId) {
      throw new Error('Authentication mismatch. Please log in again.');
    }
    
    // Format the new display name
    const newDisplayName = formatDisplayName(firstName, lastName);
    
    if (!newDisplayName) {
      throw new Error('Invalid name format');
    }
    
    // Store original displayName for rollback
    const originalDisplayName = currentUser.displayName;
    
    try {
      // Update Firebase Auth displayName first
      await updateProfile(currentUser, { displayName: newDisplayName });
      console.log(`Updated Firebase Auth displayName to: ${newDisplayName}`);
    } catch (authError) {
      console.error('Failed to update Firebase Auth displayName:', authError);
      throw new Error('Failed to update authentication profile. Please try again.');
    }
    
    try {
      // Update Firestore profile
      const profileRef = doc(db, "user_profiles", userId);
      await updateDoc(profileRef, {
        displayName: newDisplayName,
        'basicInfo.firstName': firstName,
        'basicInfo.lastName': lastName,
        'basicInfo.name': newDisplayName,
        lastUpdated: serverTimestamp()
      });
      
      console.log('User name updated successfully');
    } catch (firestoreError) {
      console.error('Failed to update Firestore profile:', firestoreError);
      
      // Attempt to rollback Firebase Auth change
      try {
        await updateProfile(currentUser, { displayName: originalDisplayName });
        console.log('Rolled back Firebase Auth displayName');
      } catch (rollbackError) {
        console.error('Failed to rollback Firebase Auth change:', rollbackError);
      }
      
      throw new Error('Failed to update profile data. Please try again.');
    }
    
    return {
      success: true,
      data: {
        firstName,
        lastName,
        displayName: newDisplayName
      }
    };
  } catch (error) {
    console.error("Error updating user name:", error);
    return { 
      success: false, 
      error: error.message || 'An unexpected error occurred while updating your name'
    };
  }
};

// Save video metadata directly to user profile
export const saveVideoToUserProfile = async (userId, videoData, videoType = 'intro') => {
  try {
    console.log(`Saving ${videoType} video metadata to user profile for user ${userId}`);
    
    // Reference to the user profile document
    const profileRef = doc(db, "user_profiles", userId);
    
    // First check if the profile exists
    const profileSnap = await getDoc(profileRef);
    
    // Create the video submission data with type
    const videoSubmissionData = {
      videoUrl: videoData.videoUrl,
      storagePath: videoData.storagePath,
      timestamp: videoData.timestamp,
      videoType: videoType, // Add video type to identify different videos
      lastUpdated: serverTimestamp()
    };

    if (!profileSnap.exists()) {
      // Create a new profile if it doesn't exist
      await setDoc(profileRef, {
        userId,
        videos: {
          [videoType]: videoSubmissionData // Store as an object with videoType as key
        },
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
    } else {
      // For backward compatibility
      if (videoType === 'intro') {
        // Also update the old videoSubmission field
        await updateDoc(profileRef, {
          videoSubmission: videoSubmissionData,
          [`videos.${videoType}`]: videoSubmissionData,
          lastUpdated: serverTimestamp()
        });
      } else {
        // If it's not an intro video, just update the videos collection
        await updateDoc(profileRef, {
          [`videos.${videoType}`]: videoSubmissionData,
          lastUpdated: serverTimestamp()
        });
      }
    }
    
    console.log(`${videoType} video metadata saved to user profile successfully`);
    
    return { 
      success: true, 
      data: {
        videoUrl: videoData.videoUrl,
        storagePath: videoData.storagePath,
        timestamp: videoData.timestamp,
        videoType: videoType
      }
    };
  } catch (error) {
    console.error(`Error saving ${videoType} video to user profile:`, error);
    return { success: false, error: error.message };
  }
};

// Create a basic user profile for new users
export const createUserProfile = async (userId) => {
  try {
    console.log('Creating new profile for user:', userId);
    
    // Get user information from auth
    const user = auth.currentUser;
    
    if (!user) {
      console.error('No authenticated user found to create profile');
      return { success: false, error: "No authenticated user found" };
    }
    
    const profileRef = doc(db, "user_profiles", userId);
    
    // Parse names from displayName using utility function
    const { firstName, lastName } = parseDisplayName(user.displayName);
    
    // Create a basic user profile
    const userData = {
      uid: userId,
      userId: userId,
      email: user.email || null,
      displayName: user.displayName || null,
      photoURL: user.photoURL || null,
      phoneNumber: user.phoneNumber || null,
      basicInfo: {
        email: user.email || null,
        name: user.displayName || null,
        firstName: firstName,
        lastName: lastName
      },
      // Empty structures for data that will be filled later
      preferences: {},
      videoResponses: {},
      profileLinks: {},
      // Default notification preferences for new users
      notificationPreferences: {
        email: {
          enabled: true
        },
        forum: {
          replyNotifications: true,
          likeNotifications: true,
          digestMode: false,
          digestTime: "09:00",
          enabled: true
        },
        emailInfo: {
          verified: user.emailVerified || false,
          address: user.email || '',
          bounceCount: 0,
          lastBounce: null
        }
      },
      unsubscribeToken: uuidv4(),
      // Timestamps and metadata
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp(),
      emailVerified: user.emailVerified,
      isAdmin: user.email === 'grey@justburnt.com' || 
              user.email === 'arjan@justburnt.com' || 
              user.email === 'louie@justburnt.com',
      lastUpdated: serverTimestamp()
    };
    
    // Create the user profile document
    await setDoc(profileRef, userData);
    console.log(`Created new user profile for ${userId}`);
    
    // Also create an empty profile summary
    const profileSummaryRef = doc(db, "user_profile_summaries", userId);
    const profileSummarySnap = await getDoc(profileSummaryRef);
    
    if (!profileSummarySnap.exists()) {
      const defaultProfileSummary = {
        userID: userId,
        profileSummary: "",
        keyHighlights: {
          role: "",
          industries: [],
          strengths: []
        },
        idealRoleCharacteristics: {
          roleScope: "",
          keyMotivators: "",
          industryFocus: []
        },
        keySkills: [],
        painPoints: [],
        potentialFit: "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(profileSummaryRef, defaultProfileSummary);
      console.log(`Created profile summary document for ${userId}`);
    }
    
    // Return the newly created profile data
    return { success: true, data: userData };
  } catch (error) {
    console.error("Error creating user profile:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Fetches all users with completed onboarding from Firestore
 * Only returns users who have onboardingComplete === true
 * Hidden users are excluded - they should only be fetched via getHiddenUsers()
 * @returns {Promise<{success: boolean, data: UserProfile[], error?: string}>}
 */
export const getAllUsers = async () => {
  try {
    console.log('Fetching all visible users with completed onboarding...');
    
    const usersRef = collection(db, 'user_profiles');
    const q = query(
      usersRef, 
      where('onboardingComplete', '==', true), // CRITICAL: Only show completed profiles
      orderBy('lastUpdated', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    
    const users = [];
    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      // Double-check onboarding completion as an extra safety measure
      if (userData.onboardingComplete === true) {
        // Always filter out hidden users - they should only appear in the hidden section
        if (userData.isHidden === true) {
          return; // Skip hidden users for all users (admin and non-admin)
        }
        
        // Parse names from displayName as source of truth (consistent with sidebar)
        const { firstName, lastName } = parseDisplayName(userData.displayName);
        
        // Transform user data to ensure consistent naming and photo URL handling
        const transformedUserData = {
          id: doc.id,
          uid: doc.id, // Include uid for compatibility
          ...userData,
          basicInfo: {
            ...userData.basicInfo,
            // Use parsed names from displayName as source of truth (same as sidebar)
            firstName: firstName || userData.basicInfo?.firstName || '',
            lastName: lastName || userData.basicInfo?.lastName || '',
            // Consistent photo URL priority: prefer custom uploaded profilePicture, then basicInfo.photoURL, then Firebase Auth photoURL
            photoURL: userData.profilePicture || userData.basicInfo?.photoURL || userData.photoURL || ''
          }
        };
        
        users.push(transformedUserData);
      }
    });
    
    console.log(`Retrieved ${users.length} visible users with completed onboarding`);
    
    return { success: true, data: users };
  } catch (error) {
    console.error('Error fetching users:', error);
    return { success: false, error: error.message, data: [] };
  }
};

// Upload resume and update user profile
export const uploadResume = async (userId, submissionId, file, shouldDelete = false) => {
  try {
    // If shouldDelete is true, remove the resume
    if (shouldDelete) {
      // Get the current profile data to find the resume storage path
      const profileRef = doc(db, "user_profiles", userId);
      const profileSnap = await getDoc(profileRef);
      
      if (profileSnap.exists()) {
        const data = profileSnap.data();
        
        // If there's a resume storage path, delete the file
        if (data.resumeStoragePath) {
          const { deleteObject, ref } = await import('firebase/storage');
          const { storage } = await import('./firebase-config');
          const resumeRef = ref(storage, data.resumeStoragePath);
          await deleteObject(resumeRef).catch(err => {
            console.log('Resume file may not exist in storage:', err);
          });
        }
        
        // Update the profile to remove resume data
        const updateData = {
          resumeUrl: null,
          resumeName: null,
          resumeFilename: null,
          resumeStoragePath: null,
          resumeLastUpdated: null
        };
        
        await updateDoc(profileRef, updateData);
        
        return { success: true };
      } else {
        // Create an empty profile if it doesn't exist
        await setDoc(profileRef, {
          userId: userId,
          createdAt: serverTimestamp()
        });
        return { success: true };
      }
    }
    
    // Check if we have required data
    if (!userId || !file) {
      return { success: false, error: "Missing required information" };
    }
    
    // Upload the resume using the storage module
    const { uploadResumeToStorage } = await import('./storage');
    const uploadResult = await uploadResumeToStorage(userId, file);
    
    if (!uploadResult.success) {
      return uploadResult;
    }
    
    // Update the user profile with resume data
    const profileRef = doc(db, "user_profiles", userId);
    const profileSnap = await getDoc(profileRef);
    
    // Prepare the update data
    const updateData = {
      resumeUrl: uploadResult.resumeUrl,
      resumeName: uploadResult.resumeName,
      resumeFilename: uploadResult.resumeName,
      resumeStoragePath: uploadResult.storagePath,
      resumeLastUpdated: serverTimestamp()
    };
    
    // Update or create the profile
    if (profileSnap.exists()) {
      await updateDoc(profileRef, updateData);
    } else {
      // Create new profile if it doesn't exist
      await setDoc(profileRef, {
        userId: userId,
        ...updateData,
        createdAt: serverTimestamp()
      });
    }
    
    return {
      success: true,
      resumeUrl: uploadResult.resumeUrl,
      resumeName: uploadResult.resumeName
    };
  } catch (error) {
    console.error("Error uploading resume:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Hide a user from the public directory (admin only)
 * @param {string} userId - ID of the user to hide
 * @param {string} adminEmail - Email of the admin performing the action
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const hideUser = async (userId, adminEmail) => {
  try {
    // Validate admin permissions
    if (!isAdminEmail(adminEmail)) {
      console.error('Unauthorized attempt to hide user by:', adminEmail);
      return { success: false, error: 'Unauthorized: Admin access required' };
    }

    console.log(`Admin ${adminEmail} hiding user: ${userId}`);
    
    const userRef = doc(db, 'user_profiles', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      return { success: false, error: 'User not found' };
    }
    
    const userData = userSnap.data();
    
    // Check if user is already hidden
    if (userData.isHidden === true) {
      return { success: false, error: 'User is already hidden' };
    }
    
    // Update user document to hide them
    await updateDoc(userRef, {
      isHidden: true,
      hiddenAt: serverTimestamp(),
      hiddenBy: adminEmail,
      // Clear unhidden fields if they exist
      unhiddenAt: null,
      unhiddenBy: null,
      lastUpdated: serverTimestamp()
    });
    
    console.log(`User ${userId} successfully hidden by admin ${adminEmail}`);
    return { success: true };
  } catch (error) {
    console.error('Error hiding user:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Unhide a user from the public directory (admin only)
 * @param {string} userId - ID of the user to unhide
 * @param {string} adminEmail - Email of the admin performing the action
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const unhideUser = async (userId, adminEmail) => {
  try {
    // Validate admin permissions
    if (!isAdminEmail(adminEmail)) {
      console.error('Unauthorized attempt to unhide user by:', adminEmail);
      return { success: false, error: 'Unauthorized: Admin access required' };
    }

    console.log(`Admin ${adminEmail} unhiding user: ${userId}`);
    
    const userRef = doc(db, 'user_profiles', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      return { success: false, error: 'User not found' };
    }
    
    const userData = userSnap.data();
    
    // Check if user is already visible
    if (userData.isHidden !== true) {
      return { success: false, error: 'User is already visible' };
    }
    
    // Update user document to unhide them
    await updateDoc(userRef, {
      isHidden: false,
      unhiddenAt: serverTimestamp(),
      unhiddenBy: adminEmail,
      lastUpdated: serverTimestamp()
    });
    
    console.log(`User ${userId} successfully unhidden by admin ${adminEmail}`);
    return { success: true };
  } catch (error) {
    console.error('Error unhiding user:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all hidden users (admin only)
 * @param {string} adminEmail - Email of the admin requesting hidden users
 * @returns {Promise<{success: boolean, data: UserProfile[], error?: string}>}
 */
export const getHiddenUsers = async (adminEmail) => {
  try {
    // Validate admin permissions
    if (!isAdminEmail(adminEmail)) {
      console.error('Unauthorized attempt to get hidden users by:', adminEmail);
      return { success: false, error: 'Unauthorized: Admin access required', data: [] };
    }

    console.log(`Admin ${adminEmail} fetching hidden users...`);
    
    const usersRef = collection(db, 'user_profiles');
    const q = query(
      usersRef,
      where('isHidden', '==', true),
      orderBy('hiddenAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    
    const hiddenUsers = [];
    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      
      // Parse names from displayName as source of truth (consistent with sidebar)
      const { firstName, lastName } = parseDisplayName(userData.displayName);
      
      // Transform user data to ensure consistent naming and photo URL handling
      const transformedUserData = {
        id: doc.id,
        uid: doc.id,
        ...userData,
        basicInfo: {
          ...userData.basicInfo,
          // Use parsed names from displayName as source of truth (same as sidebar)
          firstName: firstName || userData.basicInfo?.firstName || '',
          lastName: lastName || userData.basicInfo?.lastName || '',
          // Consistent photo URL priority: prefer custom uploaded profilePicture, then basicInfo.photoURL, then Firebase Auth photoURL
          photoURL: userData.profilePicture || userData.basicInfo?.photoURL || userData.photoURL || ''
        }
      };
      
      hiddenUsers.push(transformedUserData);
    });
    
    console.log(`Retrieved ${hiddenUsers.length} hidden users for admin ${adminEmail}`);
    
    return { success: true, data: hiddenUsers };
  } catch (error) {
    console.error('Error fetching hidden users:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get user's notification preferences
 * @param {string} userId - The user ID
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const getUserNotificationPreferences = async (userId) => {
  try {
    console.log(`Fetching notification preferences for user: ${userId}`);
    
    const profileRef = doc(db, "user_profiles", userId);
    const profileSnap = await getDoc(profileRef);
    
    if (!profileSnap.exists()) {
      console.log('No profile document found for this user');
      return {
        success: false,
        error: 'User profile not found'
      };
    }
    
    const userData = profileSnap.data();
    
    // Return notification preferences with defaults if not set
    const defaultPreferences = {
      email: {
        enabled: true
      },
      forum: {
        replyNotifications: true,
        likeNotifications: true,
        tagNotifications: true,
        digestMode: false,
        digestTime: "09:00",
        enabled: true
      },
      emailInfo: {
        verified: userData.emailVerified || false,
        address: userData.email || userData.basicInfo?.email || '',
        bounceCount: 0,
        lastBounce: null
      }
    };
    
    const preferences = {
      ...defaultPreferences,
      ...userData.notificationPreferences
    };
    
    // Ensure unsubscribe token exists
    let unsubscribeToken = userData.unsubscribeToken;
    if (!unsubscribeToken) {
      unsubscribeToken = uuidv4();
      await updateDoc(profileRef, {
        unsubscribeToken,
        lastUpdated: serverTimestamp()
      });
    }
    
    return {
      success: true,
      data: {
        preferences,
        unsubscribeToken
      }
    };
  } catch (error) {
    console.error("Error fetching notification preferences:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get user's status and permissions
 * @param {string} userId - The user ID
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const getUserStatus = async (userId) => {
  try {
    console.log(`Fetching status for user: ${userId}`);
    
    const profileRef = doc(db, "user_profiles", userId);
    const profileSnap = await getDoc(profileRef);
    
    if (!profileSnap.exists()) {
      return {
        success: false,
        error: 'User profile not found'
      };
    }
    
    const userData = profileSnap.data();
    
    return {
      success: true,
      data: {
        status: userData.status || 'active', // Default to active for existing users
        emailVerified: userData.emailVerified || false,
        onboardingComplete: userData.onboardingComplete || false,
        isAdmin: userData.isAdmin || false,
        waitlistMetadata: userData.waitlistMetadata || null
      }
    };
  } catch (error) {
    console.error("Error fetching user status:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Update user's theme preference
 * @param {string} userId - The user ID
 * @param {string} theme - The theme preference ('light' or 'dark')
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateUserThemePreference = async (userId, theme) => {
  try {
    console.log(`Updating theme preference for user: ${userId} to ${theme}`);
    
    // Validate theme value
    if (!theme || !['light', 'dark'].includes(theme)) {
      return { success: false, error: 'Invalid theme value. Must be "light" or "dark"' };
    }
    
    const profileRef = doc(db, "user_profiles", userId);
    const profileSnap = await getDoc(profileRef);
    
    if (!profileSnap.exists()) {
      return { success: false, error: 'User profile not found' };
    }
    
    // Update the profile with theme preference
    await updateDoc(profileRef, {
      themePreference: theme,
      lastUpdated: serverTimestamp()
    });
    
    console.log('Theme preference updated successfully');
    
    return { success: true };
  } catch (error) {
    console.error("Error updating theme preference:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Update user's notification preferences
 * @param {string} userId - The user ID
 * @param {object} preferences - The notification preferences to update
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateNotificationPreferences = async (userId, preferences) => {
  try {
    console.log(`Updating notification preferences for user: ${userId}`);
    
    // Validate preferences structure
    if (!preferences || typeof preferences !== 'object') {
      return { success: false, error: 'Invalid preferences data' };
    }
    
    const profileRef = doc(db, "user_profiles", userId);
    const profileSnap = await getDoc(profileRef);
    
    if (!profileSnap.exists()) {
      return { success: false, error: 'User profile not found' };
    }
    
    // Validate preference values
    const validatedPreferences = {};
    
    if (preferences.email) {
      validatedPreferences.email = {
        enabled: Boolean(preferences.email.enabled)
      };
    }
    
    if (preferences.forum) {
      validatedPreferences.forum = {};
      
      if (preferences.forum.replyNotifications !== undefined) {
        validatedPreferences.forum.replyNotifications = Boolean(preferences.forum.replyNotifications);
      }
      
      if (preferences.forum.likeNotifications !== undefined) {
        validatedPreferences.forum.likeNotifications = Boolean(preferences.forum.likeNotifications);
      }
      
      if (preferences.forum.tagNotifications !== undefined) {
        validatedPreferences.forum.tagNotifications = Boolean(preferences.forum.tagNotifications);
      }
      
      if (preferences.forum.digestMode !== undefined) {
        validatedPreferences.forum.digestMode = Boolean(preferences.forum.digestMode);
      }
      
      if (preferences.forum.digestTime !== undefined) {
        // Validate time format (HH:MM)
        const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
        if (timeRegex.test(preferences.forum.digestTime)) {
          validatedPreferences.forum.digestTime = preferences.forum.digestTime;
        }
      }
      
      if (preferences.forum.enabled !== undefined) {
        validatedPreferences.forum.enabled = Boolean(preferences.forum.enabled);
      }
    }
    
    // Update the profile with validated preferences
    await updateDoc(profileRef, {
      notificationPreferences: validatedPreferences,
      lastUpdated: serverTimestamp()
    });
    
    console.log('Notification preferences updated successfully');
    
    return { success: true };
  } catch (error) {
    console.error("Error updating notification preferences:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Initialize default notification preferences for existing users
 * @param {string} userId - The user ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const initializeNotificationPreferences = async (userId) => {
  try {
    console.log(`Initializing notification preferences for user: ${userId}`);
    
    const profileRef = doc(db, "user_profiles", userId);
    const profileSnap = await getDoc(profileRef);
    
    if (!profileSnap.exists()) {
      return { success: false, error: 'User profile not found' };
    }
    
    const userData = profileSnap.data();
    
    // Only initialize if preferences don't already exist
    if (userData.notificationPreferences) {
      console.log('Notification preferences already exist for this user');
      return { success: true };
    }
    
    const defaultPreferences = {
      email: {
        enabled: true
      },
      forum: {
        replyNotifications: true,
        likeNotifications: true,
        digestMode: false,
        digestTime: "09:00",
        enabled: true
      },
      emailInfo: {
        verified: userData.emailVerified || false,
        address: userData.email || userData.basicInfo?.email || '',
        bounceCount: 0,
        lastBounce: null
      }
    };
    
    // Generate unsubscribe token if it doesn't exist
    let unsubscribeToken = userData.unsubscribeToken;
    if (!unsubscribeToken) {
      unsubscribeToken = uuidv4();
    }
    
    await updateDoc(profileRef, {
      notificationPreferences: defaultPreferences,
      unsubscribeToken,
      lastUpdated: serverTimestamp()
    });
    
    console.log('Default notification preferences initialized successfully');
    
    return { success: true };
  } catch (error) {
    console.error("Error initializing notification preferences:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Update email bounce information for a user
 * @param {string} userId - The user ID
 * @param {boolean} bounced - Whether the email bounced
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateEmailBounceInfo = async (userId, bounced = true) => {
  try {
    console.log(`Updating email bounce info for user: ${userId}`);
    
    const profileRef = doc(db, "user_profiles", userId);
    const profileSnap = await getDoc(profileRef);
    
    if (!profileSnap.exists()) {
      return { success: false, error: 'User profile not found' };
    }
    
    const userData = profileSnap.data();
    const currentBounceCount = userData.notificationPreferences?.emailInfo?.bounceCount || 0;
    
    const updateData = {
      'notificationPreferences.emailInfo.lastBounce': bounced ? serverTimestamp() : null,
      lastUpdated: serverTimestamp()
    };
    
    if (bounced) {
      const newBounceCount = currentBounceCount + 1;
      updateData['notificationPreferences.emailInfo.bounceCount'] = newBounceCount;
      
      // Disable email notifications after 3 bounces
      if (newBounceCount >= 3) {
        updateData['notificationPreferences.email.enabled'] = false;
        console.log(`Disabled email notifications for user ${userId} after ${newBounceCount} bounces`);
      }
    } else {
      // Reset bounce count on successful delivery
      updateData['notificationPreferences.emailInfo.bounceCount'] = 0;
    }
    
    await updateDoc(profileRef, updateData);
    
    console.log('Email bounce info updated successfully');
    
    return { success: true };
  } catch (error) {
    console.error("Error updating email bounce info:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Unsubscribe user from email notifications using token
 * @param {string} userId - The user ID
 * @param {string} token - The unsubscribe token
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const unsubscribeFromNotifications = async (userId, token) => {
  try {
    console.log(`Processing unsubscribe request for user: ${userId}`);
    
    if (!userId || !token) {
      return { success: false, error: 'User ID and token are required' };
    }
    
    const profileRef = doc(db, "user_profiles", userId);
    const profileSnap = await getDoc(profileRef);
    
    if (!profileSnap.exists()) {
      return { success: false, error: 'User profile not found' };
    }
    
    const userData = profileSnap.data();
    
    // Verify unsubscribe token
    if (userData.unsubscribeToken !== token) {
      console.log('Invalid unsubscribe token provided');
      return { success: false, error: 'Invalid unsubscribe token' };
    }
    
    // Disable all email notifications
    await updateDoc(profileRef, {
      'notificationPreferences.email.enabled': false,
      'notificationPreferences.forum.enabled': false,
      unsubscribedAt: serverTimestamp(),
      lastUpdated: serverTimestamp()
    });
    
    console.log(`Successfully unsubscribed user ${userId} from email notifications`);
    
    return { success: true };
  } catch (error) {
    console.error("Error unsubscribing from notifications:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Validate unsubscribe token
 * @param {string} userId - The user ID
 * @param {string} token - The unsubscribe token
 * @returns {Promise<{success: boolean, valid?: boolean, error?: string}>}
 */
export const validateUnsubscribeToken = async (userId, token) => {
  try {
    console.log(`Validating unsubscribe token for user: ${userId}`);
    
    if (!userId || !token) {
      return { success: false, error: 'User ID and token are required' };
    }
    
    const profileRef = doc(db, "user_profiles", userId);
    const profileSnap = await getDoc(profileRef);
    
    if (!profileSnap.exists()) {
      return { success: false, error: 'User profile not found' };
    }
    
    const userData = profileSnap.data();
    const isValid = userData.unsubscribeToken === token;
    
    return {
      success: true,
      valid: isValid
    };
  } catch (error) {
    console.error("Error validating unsubscribe token:", error);
    return { success: false, error: error.message };
  }
};